import React, { useState } from 'react';
import { Modal } from '../Modal';
import Spinner from '../Spinner';
import MatchImage from '../../assets/images/matchi/match.png';
import CoffeeChat from '../../assets/svg/coffee-chat.svg';

export type CoffeeChatMode = 'request' | 'accept' | 'both-accepted';

interface CoffeeChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  matchName: string;
  matchAvatar: string;
  matchLocation?: string;
  matchBio?: string;
  onAccept: () => void;
  onRequestAnother: () => void;
  mode: CoffeeChatMode;
  isLoading?: boolean;
}

const CoffeeChatModal: React.FC<CoffeeChatModalProps> = ({
  isOpen,
  onClose,
  matchName,
  matchAvatar,
  matchLocation,
  matchBio,
  onAccept,
  onRequestAnother,
  mode,
  isLoading = false
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const getContent = () => {
    switch (mode) {
      case 'request':
        return {
          title: 'Ready for a Coffee Chat?',
          subtitle: 'Matchi worked hard, and voilà—a match\nfor your next Coffee Chat!',
          primaryButton: 'Request Coffee Chat',
          secondaryButton: 'Request Another Match'
        };
      case 'accept':
        return {
          title: 'Coffee Chat Request!',
          subtitle: `${matchName} would like to have\na coffee chat with you!`,
          primaryButton: 'Accept Coffee Chat',
          secondaryButton: 'Maybe Later'
        };
      case 'both-accepted':
        return {
          title: 'Coffee Chat Accepted! 🎉',
          subtitle: 'You both said yes!\nTime to start chatting and plan your coffee meet-up.',
          primaryButton: 'Start Chatting',
          secondaryButton: 'Close'
        };
      default:
        return {
          title: 'Ready for a Coffee Chat?',
          subtitle: 'Matchi worked hard, and voilà—a match\nfor your next Coffee Chat!',
          primaryButton: 'Request Coffee Chat',
          secondaryButton: 'Request Another Match'
        };
    }
  };

  const toggleShowMore = () => {
    setIsExpanded(!isExpanded);
  };

  const renderBioText = () => {
    if (!matchBio) return 'No bio available';
    if (isExpanded) {
      return matchBio;
    } else {
      // Show first 50 characters with ellipsis
      return matchBio.length > 50 ? `${matchBio.substring(0, 50)}...` : matchBio;
    }
  };

  const content = getContent();

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="coffee-chat-modal has-text-centered">
        <div className="modal-illustration-container mb-4">
          <img
            src={MatchImage}
            alt="Coffee Chat"
            className="modal-illustration"
          />
          <div className="profile-overlays">
            <img
              src={matchAvatar}
              alt={matchName}
              className="profile-overlay profile-overlay-left"
            />
            <img
              src={matchAvatar}
              alt="Current User"
              className="profile-overlay profile-overlay-right"
            />
          </div>
        </div>

        <h2 className="title is-4 mb-2">{content.title}</h2>
        <div className="pt-6">
          <p className="subtitle is-6 mb-4">
            {content.subtitle.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < content.subtitle.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        </div>

        <div className="match-profile-horizontal mb-4">
          <img
            src={matchAvatar}
            alt={matchName}
            className="profile-avatar"
          />
          <div className="profile-info">
            <h3 className="profile-name">{matchName}</h3>
            <p className="profile-details">
              {matchLocation ?? 'Location not available'} • {renderBioText()}
              {matchBio && matchBio.length > 50 && (
                <span
                  className="show-more-link"
                  onClick={toggleShowMore}
                >
                  {isExpanded ? ' Show Less' : ' Show More'}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="modal-buttons">
          <button
            className="button is-primary is-fullwidth coffee-chat-btn"
            onClick={onAccept}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="icon mr-2">
                  <Spinner />
                </span>
                <span className="ml-2">Processing...</span>
              </>
            ) : (
              <>
                <span className="icon mr-2">
                  <img
                    src={CoffeeChat}
                    alt="Chat icon"
                    className="match-chat-icon"/>
                </span>
                <span className="ml-2">{content.primaryButton}</span>
              </>
            )}
          </button>
          <button
            className="button is-outlined is-fullwidth another-match-btn"
            onClick={onRequestAnother}
            disabled={isLoading}
          >
            {content.secondaryButton}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CoffeeChatModal;